import type {
  FlowMeta,
  LoadedViewResponse,
  LoaderAssetsPayload,
  WireEarlyLayout,
  WireLoadingModule,
} from "@bestowinc/tinkertoy-core";

/** What the in-flight `/load` has told the shell about its loading state. */
export interface LoadingSnapshot {
  /** Changes when a new load begins; a redirect hop continues the same load. */
  loadId: number;
  /** Set once this run has said whether it has loading metadata. */
  reported?: boolean;
  loadingModule?: WireLoadingModule;
  /** `false` once the server says no early layout is coming. */
  earlyLayout?: boolean;
  layout?: WireEarlyLayout;
  loadedView?: LoadedViewResponse;
  flowMeta?: FlowMeta;
}

/**
 * Per-shell slot the loader writes streamed loading metadata into while a
 * `/load` is in flight, read by the shell with `useSyncExternalStore`.
 *
 * Each loader run owns the slot through a token from {@link begin}; writes
 * from a superseded run are ignored, so an interrupted navigation never
 * leaves stale state behind. A run that ends in a redirect hands its
 * `loadId` to the next run, so the redirect target continues the same load.
 */
export interface LoadingSlot {
  begin: () => number;
  update: (token: number, payload: LoaderAssetsPayload) => void;
  /** The run has no loading metadata to stream (JSON response, or no assets chunk). */
  reportNoLoading: (token: number) => void;
  end: (token: number, outcome?: { redirected: boolean }) => void;
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => LoadingSnapshot;
}

export function createLoadingSlot(): LoadingSlot {
  let current = 0;
  let continuing = false;
  let empty: LoadingSnapshot = { loadId: 0 };
  let snapshot = empty;
  const listeners = new Set<() => void>();

  function set(next: LoadingSnapshot): void {
    snapshot = next;
    for (const listener of listeners) {
      listener();
    }
  }

  function reportNoLoading(token: number): void {
    if (token === current && snapshot.reported !== true) {
      set({ loadId: empty.loadId, reported: true });
    }
  }

  return {
    begin: () => {
      current += 1;
      if (!continuing) {
        empty = { loadId: empty.loadId + 1 };
      }
      continuing = false;
      if (snapshot !== empty) {
        set(empty);
      }
      return current;
    },
    update: (token, payload) => {
      if (payload.loadingModule === undefined) {
        reportNoLoading(token);
        return;
      }
      if (token !== current) {
        return;
      }
      set({
        loadId: empty.loadId,
        reported: true,
        loadingModule: payload.loadingModule,
        earlyLayout: payload.earlyLayout ?? snapshot.earlyLayout,
        layout: payload.layout ?? snapshot.layout,
        loadedView: payload.loadedView,
        flowMeta: payload.flowMeta ?? snapshot.flowMeta,
      });
    },
    reportNoLoading,
    end: (token, outcome) => {
      if (token !== current) {
        return;
      }
      continuing = outcome?.redirected === true;
      if (snapshot !== empty) {
        set(empty);
      }
    },
    subscribe: listener => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => snapshot,
  };
}
